import { useSelector } from 'react-redux';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import settings from '/@/core/settings';
import { getCropMonitorings } from '/@/store/crop-monitoring/apiProviders';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance } from '/@/store/session/selectors';
import type { PaginatedResponse } from '/@/types/core';
import type { CropType } from '/@/types/crop-monitoring';

export const useCropMonitorings = ({
    fieldId,
    page,
    pageSize = 10000,
    node,
    includeChildren,
    excludeTargetNode,
    year,
    crop,
    fieldName,
    productName,
    phenology,
    active,
    ordering,
    campaign,
}: {
    page: number;
    fieldId?: string;
    pageSize?: number;
    node?: number;
    includeChildren?: boolean;
    crop?: CropType;
    excludeTargetNode?: boolean;
    year?: number;
    fieldName?: string;
    productName?: string;
    phenology?: string;
    active?: boolean;
    ordering?: string;
    campaign?: number;
}) => {
    const appInstance = useSelector(getAppInstance)?.id;
    const key = appInstance
        ? {
              key: 'allCropMonitorings',
              appInstance,
              fieldId,
              pageSize,
              page,
              node,
              includeChildren,
              excludeTargetNode,
              year,
              fieldName,
              crop,
              productName,
              phenology,
              active,
              ordering,
              campaign,
          }
        : null;

    return useSWRImmutable(
        key,
        (key) => {
            return getCropMonitorings({
                appInstanceId: key.appInstance,
                fieldId: key.fieldId,
                pageSize: key.pageSize,
                page: key.page,
                node: key.node,
                includeChildren: key.includeChildren,
                excludeTargetNode: key.excludeTargetNode,
                crop: key.crop,
                year: key.year,
                fieldName: key.fieldName,
                productName: key.productName,
                phenology: key.phenology,
                active: key.active,
                ordering: key.ordering,
                campaign: key.campaign,
            });
        },
        {
            revalidateOnFocus: false,
        }
    );
};

export const getCropMonitoringResult = async ({ cropMonitoringId }: { cropMonitoringId: number }) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/result/`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useCropMonitoringResult = (
    { cropMonitoringId }: { cropMonitoringId: number },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    let key = null;
    if (enabled && cropMonitoringId) key = { cropMonitoringId, key: 'useCropMonitoringResult' };

    return useSWR(key, getCropMonitoringResult);
};

// Crop Monitoring Results List
export interface CropMonitoringResultsListParams {
    page?: number;
    pageSize?: number;
    ordering?: string;
    createdBy?: number;
    updatedBy?: number;
    created?: string;
    createdGte?: string;
    createdLte?: string;
    node?: number;
    includeChildren?: boolean;
}

export interface CropMonitoringResultListItem {
    id: number;
    cropMonitoring: number;
    plantingDate?: string;
    harvestDate?: string;
    humidityAtHarvest?: number;
    averageYield?: number;
    created: string;
    modified: string;
    createdBy?: number;
    updatedBy?: number;
    userNode?: number;
    parentUserNode?: number;
}

export const getCropMonitoringResults = async (params: CropMonitoringResultsListParams & { key: string }) => {
    const { ...queryParams } = params;
    const searchParams = new URLSearchParams();

    if (queryParams.page) searchParams.append('page', String(queryParams.page));
    if (queryParams.pageSize) searchParams.append('page_size', String(queryParams.pageSize));
    if (queryParams.ordering) searchParams.append('ordering', queryParams.ordering);
    if (queryParams.createdBy) searchParams.append('created_by', String(queryParams.createdBy));
    if (queryParams.updatedBy) searchParams.append('updated_by', String(queryParams.updatedBy));
    if (queryParams.node) searchParams.append('node', String(queryParams.node));
    if (queryParams.includeChildren !== undefined)
        searchParams.append('include_children', String(queryParams.includeChildren));

    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/results/?${searchParams.toString()}`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useCropMonitoringResults = (
    params: CropMonitoringResultsListParams = {},
    options: { enabled?: boolean } = { enabled: true }
) => {
    const key = options.enabled
        ? {
              key: 'useCropMonitoringResults',
              ...params,
          }
        : null;

    return useSWR<PaginatedResponse<CropMonitoringResultListItem>>(key, getCropMonitoringResults, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        keepPreviousData: true,
    });
};
